import check from '../libraries/check-types-ext';
import AppError from '../libraries/AppError';
import { ApmHelper, ApmSpanType } from '../libraries/ApmHelper';
import { pick } from 'lodash';
import Product from './Product';
export default class ProductMapper {
    constructor() {
        this.productMap = {};
        this.productNameMap = {};
        this.productNameDuplicateMap = {};
        this.categoryMap = {};
        this.initSubscriberMap = {};
        this.correspondingRootProducts = {};
        this.productSeoOptimizedNameToIdMap = {};
        this.productCache = new Map();
    }
    getProductObject(productNode, context) {
        const id = 'id' in productNode ? productNode.id : productNode.id_ref;
        const cacheKey = context ? `product-${id}-${context}` : `product-${id}`;
        if (!this.productCache.has(cacheKey)) {
            const product = new Product(productNode, this);
            this.productCache.set(cacheKey, product);
            return product;
        }
        const pp = this.productCache.get(cacheKey);
        if (!pp.id && 'id' in productNode && productNode.id) {
            const product = new Product(productNode, this);
            this.productCache.set(cacheKey, product);
            return product;
        }
        return pp;
    }
    resolveProduct(productId, initFunction) {
        return ApmHelper.apmDecorator(() => {
            if (this.productMap[productId]) {
                initFunction(this.productMap[productId]);
            }
            else {
                if (!this.initSubscriberMap[productId]) {
                    this.initSubscriberMap[productId] = [];
                }
                this.initSubscriberMap[productId].push(initFunction);
            }
        }, {
            apmLabel: 'ProductMapperResolveProduct',
            apmType: ApmSpanType.PURE_FUNCTION,
            loggingEnabled: false,
        })();
    }
    getDuplicateProducts() {
        return Object.keys(this.productNameDuplicateMap)
            .map((key) => {
            return { name: key, ids: this.productNameDuplicateMap[key] };
        })
            .filter((pair) => pair.ids.length > 1);
    }
    getCorrespondingRootProduct(productId) {
        if (this.correspondingRootProducts[productId] && this.correspondingRootProducts[productId].length > 0) {
            if (this.correspondingRootProducts[productId].length === 1) {
                return this.correspondingRootProducts[productId][0];
            }
            else {
                throw AppError.ProductBelongsToMultipleRootProducts().addData('productId', productId);
            }
        }
        else {
            return undefined;
        }
    }
    registerProduct(product) {
        return ApmHelper.apmDecorator(() => {
            const id = product.id;
            check.string.orFail(id, 'productId');
            if (this.productMap[id]) {
                return;
            }
            this.productMap[id] = product;
            const nameLower = product.name ? product.name.toLowerCase() : '';
            this.productNameMap[nameLower] = id;
            this.productSeoOptimizedNameToIdMap[product.seoOptimizedName] = id;
            if (!this.productNameDuplicateMap[nameLower]) {
                this.productNameDuplicateMap[nameLower] = [];
            }
            this.productNameDuplicateMap[nameLower].push(id);
            if (this.initSubscriberMap[id]) {
                this.initSubscriberMap[id].forEach((init) => {
                    init(product);
                });
                delete this.initSubscriberMap[id];
            }
        }, {
            apmLabel: 'ProductMapperRegisterProduct',
            apmType: ApmSpanType.PURE_FUNCTION,
            loggingEnabled: false,
        })();
    }
    registerCorrespondingRootProductRelationship(productId, rootProductId) {
        check.string.orFail(productId, 'productId');
        check.string.orFail(rootProductId, 'rootProductId');
        if (!this.correspondingRootProducts[productId]) {
            this.correspondingRootProducts[productId] = [];
        }
        this.correspondingRootProducts[productId].push(rootProductId);
    }
    registerCategory(category) {
        const extId = category.extId;
        if (this.categoryMap[extId]) {
            throw AppError.CategoryIsDuplicatedError().addData('categoryId', extId);
        }
        this.categoryMap[extId] = category;
    }
    getProduct(productId) {
        return ApmHelper.apmDecorator(() => {
            if (!this.productMap[productId]) {
                throw AppError.ProductIsUndefinedError().addData('productId', productId);
            }
            return this.productMap[productId];
        }, {
            apmLabel: 'ProductMapperGetProduct',
            apmType: ApmSpanType.PURE_FUNCTION,
            loggingEnabled: false,
        })();
    }
    getProducts(productIds) {
        return ApmHelper.apmDecorator(() => {
            return pick(this.productMap, productIds);
        }, {
            apmLabel: 'ProductMapperGetProduct',
            apmType: ApmSpanType.PURE_FUNCTION,
            loggingEnabled: false,
        })();
    }
    getProductIdByName(_name) {
        const name = _name ? _name.toLowerCase() : '';
        if (!this.productNameMap[name]) {
            throw AppError.ProductIsUndefinedError().addData('name', name);
        }
        return this.productNameMap[name];
    }
    getProductIdBySeoOptimizedName(_name) {
        const name = _name ? _name.toLowerCase() : '';
        if (!this.productSeoOptimizedNameToIdMap[name]) {
            throw AppError.ProductIsUndefinedError().addData('name', name);
        }
        return this.productSeoOptimizedNameToIdMap[name];
    }
    getCategory(categoryId) {
        if (!this.categoryMap[categoryId]) {
            throw AppError.CategoryIsUndefinedError().addData('categoryId', categoryId);
        }
        return this.categoryMap[categoryId];
    }
    removeCategory(categoryId) {
        if (!this.categoryMap[categoryId]) {
            throw AppError.CategoryIsUndefinedError().addData('categoryId', categoryId);
        }
        delete this.categoryMap[categoryId];
    }
    getHomePageCategory({ shopId = undefined, channel } = {}) {
        const homeCategories = Object.values(this.categoryMap).filter((category) => category.isHomeCategory === true);
        const filteredChannelHomeCategories = homeCategories.filter((category) => {
            if (!Array.isArray(category.isSpecificForChannels)) {
                return false;
            }
            return category.isSpecificForChannels.includes(channel);
        });
        const filteredShopHomeCategories = (filteredChannelHomeCategories.length > 0 ? filteredChannelHomeCategories : homeCategories).filter((c) => {
            if (shopId !== undefined) {
                if (!Array.isArray(c.isSpecificForShops)) {
                    return false;
                }
                if (!c.isSpecificForShops.includes(shopId)) {
                    return false;
                }
            }
            return true;
        });
        if (filteredShopHomeCategories.length > 0) {
            return filteredShopHomeCategories[0];
        }
        else if (filteredChannelHomeCategories.length > 0) {
            return filteredChannelHomeCategories[0];
        }
        else {
            return homeCategories[0];
        }
    }
    getPendingSubscriptions() {
        return Object.keys(this.initSubscriberMap);
    }
}
